// Shared 12-hour class-time formatter used across the Parent Portal
// (dashboard, classes list, upcoming, class detail, schedule, attendance,
// messages that include a schedule snippet).
//
// Input tolerance is deliberate — the backend has historically returned
// class times as any of these:
//   "19:00:00"        MySQL TIME column
//   "19:00"           trimmed TIME
//   "19.00"           legacy dot-separated value seen in the wild
//   "2026-07-24T19:00:00Z"   ISO timestamp (rare, from calendar payloads)
//   Date object
//
// Anything unrecognisable returns "" so callers can render "—" or omit
// the row entirely instead of surfacing garbage.
export function to12Hour(input: string | Date | null | undefined): string {
  if (input === null || input === undefined || input === "") return "";
  let hours = -1;
  let minutes = 0;

  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return "";
    hours = input.getHours();
    minutes = input.getMinutes();
  } else {
    const raw = String(input).trim();
    // ISO timestamp — extract the time portion.
    if (raw.includes("T")) {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return "";
      hours = d.getHours();
      minutes = d.getMinutes();
    } else {
      // Accept ":" or "." separator, ignore seconds.
      const parts = raw.split(/[:.]/);
      const h = Number(parts[0]);
      const m = Number(parts[1] ?? 0);
      if (!Number.isFinite(h) || h < 0 || h > 23) return "";
      if (!Number.isFinite(m) || m < 0 || m > 59) return "";
      hours = h;
      minutes = m;
    }
  }

  const suffix = hours >= 12 ? "PM" : "AM";
  const h12 = hours % 12 || 12;
  return `${h12}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

// Format a start/end pair as "7:00 PM – 8:00 PM" using an en-dash. If
// either end is missing, falls back to whichever side is present.
export function formatTimeRange(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined
): string {
  const s = to12Hour(start);
  const e = to12Hour(end);
  if (s && e) return `${s} – ${e}`;
  return s || e || "";
}
